// src/generators/dzial5_trygonometria/temat2_funkcje_rozszerzone/podtemat2_wzory_redukcyjne_types/ReduceSinExactTask.js
import React from "react";
import { BlockMath, InlineMath } from "react-katex";
import RootFractionInput, { parseRootFractionInput, formatLatexPreview } from "../../../../components/inputs/RootFractionInput";

// ===== helpers =====
function expectedNumeric(acuteAngle) {
  switch (acuteAngle) {
    case 0: return 0;
    case 30: return 1 / 2;
    case 45: return Math.SQRT2 / 2;
    case 60: return Math.sqrt(3) / 2;
    case 90: return 1;
    default: return NaN;
  }
}
function normalizeLatex(str) {
  return String(str)
    .replace(/\s/g, "")
    .replace(/\*/g, "")
    .replace(/sqrt\(([^)]+)\)/g, "√$1")
    .replace(/\\sqrt{([^}]+)}/g, "√$1")
    .replace(/\\dfrac{([^}]+)}{([^}]+)}/g, "$1/$2")
    .replace(/\\frac{([^}]+)}{([^}]+)}/g, "$1/$2")
    .replace(/[()]/g, "")
    .replace(/\\/g, "");
}

// ===== task =====
export function generateTask() {
  const angles = [120, 135, 150, 180];
  const alpha = angles[Math.floor(Math.random() * angles.length)];

  let acuteAngle, exactValue;
  if (alpha === 180) {
    acuteAngle = 0;
    exactValue = "0";
  } else {
    acuteAngle = 180 - alpha;
    const sinValues = {
      0: "0",
      30: "\\frac{1}{2}",
      45: "\\frac{\\sqrt{2}}{2}",
      60: "\\frac{\\sqrt{3}}{2}",
      90: "1",
    };
    exactValue = sinValues[acuteAngle];
  }

  return {
    type: "ReduceSinExactTask",
    data: { alpha, acuteAngle, exactValue }
  };
}

export function renderTask({ data }) {
  const { alpha } = data;
  return (
    <div style={{ marginBottom: "1rem" }}>
      Zredukuj kąt i oblicz dokładną wartość:
      <InlineMath math={`\\ \\sin ${alpha}^\\circ`} />
    </div>
  );
}

export function renderInput(task, userAnswer, onChange, disabled) {
  return (
    <RootFractionInput
      value={userAnswer}
      onChange={onChange}
      disabled={disabled}
      label={`\\sin ${task?.data?.alpha}^\\circ`}
    />
  );
}

export function checkAnswer({ data }, userAnswer) {
  const { alpha, acuteAngle, exactValue } = data;

  // meta do dynamicznego kąta w renderUserAnswer
  if (typeof userAnswer === "object") {
    userAnswer.func = "sin";
    userAnswer.angle = alpha;
  }

  // 1) ścieżka numeryczna (akceptuj przybliżenia)
  const expectedValue = expectedNumeric(acuteAngle);
  const raw = typeof userAnswer === "object" ? userAnswer.raw : userAnswer;
  const parsed =
    typeof userAnswer === "object" && userAnswer.parsed != null
      ? userAnswer.parsed
      : parseRootFractionInput(raw);
  if (parsed != null && !Number.isNaN(expectedValue)) {
    if (Math.abs(parsed - expectedValue) < 0.01) return true;
  }

  // 2) fallback: porównanie latex/tekst
  if (!raw) return false;
  return normalizeLatex(raw) === normalizeLatex(exactValue);
}

export function renderAnswer({ data }) {
  const { alpha, exactValue } = data;
  return (
    <InlineMath math={`\\displaystyle \\sin ${alpha}^\\circ = ${exactValue}`} />
  );
}

export function renderUserAnswer(userAnswer) {
  const raw = typeof userAnswer === "object" ? userAnswer.raw : userAnswer;
  const angle = typeof userAnswer === "object" ? userAnswer.angle : undefined;
  if (!raw || typeof raw !== "string" || raw.trim() === "") {
    return <i>(brak odpowiedzi)</i>;
  }

  // PRZEKAZUJEMY etykietę do formatLatexPreview (żeby ono wstawiło „= …”),
  // ale NIE dopisujemy jej drugi raz ręcznie — dzięki temu nie ma dubla.
  const dynamicLabel = angle != null ? `\\sin ${angle}^\\circ` : "\\sin\\alpha";
  return (
    <InlineMath math={`\\displaystyle ${formatLatexPreview(raw, dynamicLabel)}`} />
  );
}

export function renderExplanation({ data }) {
  const { alpha, acuteAngle, exactValue } = data;
  return (
    <div className="space-y-4">
      <div>
        <strong>Krok 1:</strong> Korzystamy ze wzoru redukcyjnego:
        <BlockMath math={`\\sin(180^\\circ - \\alpha) = \\sin \\alpha`} />
      </div>
      <div>
        <strong>Krok 2:</strong> Podstawiamy <InlineMath math={`\\alpha = ${alpha}^\\circ`} />:
        <BlockMath math={`\\sin ${alpha}^\\circ = \\sin(180^\\circ - ${alpha}^\\circ)`} />
        <BlockMath math={`\\sin ${alpha}^\\circ = \\sin ${acuteAngle}^\\circ`} />
      </div>
      <div>
        <strong>Krok 3:</strong> Odczytujemy dokładną wartość:
        <BlockMath math={`\\sin ${acuteAngle}^\\circ = ${exactValue}`} />
      </div>
    </div>
  );
}
